import { Injectable } from '@angular/core';
import { CustomerLocationCreateInput } from '@customer-locations/domain/customer-location/customer-location.create';
import { CustomerLocationUpdateInput } from '@customer-locations/domain/customer-location/customer-location.update';
import { CustomerLocationsResult } from '@equipment/services/customer-locations-result';
import gql from 'graphql-tag';
import { map, take } from 'rxjs/operators';
import { CustomerLocation } from 'src/app/shared/models/customer-location';
import { CrudService } from 'src/app/shared/services/crud/crud.service';
import { GraphqlService } from '../../graphql/graphql.service';
import { CustomerLocationCategory } from '@shared/models/customer-location-category';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { TokenSessionService } from '@shared/services/token.session.service';
import { LanguageService } from '@shared/services/language.service';

@Injectable({
  providedIn: 'root'
})
export class CustomerLocationService extends CrudService<CustomerLocation, CustomerLocationCreateInput, CustomerLocationUpdateInput> {

  public constructor(private graphqlService: GraphqlService, private httpClient: HttpClient, private tokenSessionService: TokenSessionService, private languageService: LanguageService) {
    super();
    this.setFragment();
  }
  public fragment: any;
  public modelName = 'CustomerLocation';
  private customerLocationForDashboardMapPinQuery: any = gql`
    query getCustomerLocationForDashboardMapPin($customerLocationId: String!) {
      getCustomerLocationForDashboardMapPin(customerLocationId: $customerLocationId) {
          name
          streetAndNumber
          city
          postalCode
          installer {
            logo
            name
            phone
          }
          equipments {
            id
            name
            status {
              color
              icon
            }
          }
      }
    }
  `;


  private customerLocationCategoriesQuery: any = gql`
    query getCustomerLocationsCategories {
      getCustomerLocationsCategories {
            id
      }
    }
  `;

  private customerLocationsQuery: any = gql`
    query getCustomerLocations($unsubscribeBefore:DateTime,$installersOrInstallerChains:[String!],$customerChains : [String!], $userId: String,$is_active: Boolean, $name: String, $sortField: String, $sortDir: String, $page: Float, $perPage: Float) {
      getCustomerLocations(criteria: {unsubscribeBefore:$unsubscribeBefore,installersOrInstallerChains:$installersOrInstallerChains, customerChains:$customerChains,userId:$userId,is_active: $is_active, name: $name, sortField: $sortField, sortDir: $sortDir, page: $page, perPage: $perPage }) {
        itemsPage {
          _id
          name
          is_active
          category
          installer {
            _id
            name
          }
          chain {
            _id
            name
          }
          dateCreated
          phone
          email
          country
          countryISO
          postalCode
          city
          latitude
          longitude
          streetAndNumber
          manager {
            _id
            name
          }
          equipments {
            _id
          }
          subscriptioncls {
            _id
            idCustomerLocation
            is_active
            subscriptionDate
            unSubscriptionDate
            subType {
              subtype
              description
              label
            }
          }
          totalEquipments
        }
        total
      }
    }
  `;
  public customerLocationsExportCsv(criteria: any): Observable<any>{
    const criteriaFilter = {} as any;

    if (criteria.filters?.installersOrInstallerChains){
      criteriaFilter.installersOrInstallerChains = criteria.filters.installersOrInstallerChains;
    }
    if (criteria.name) {
      criteriaFilter.name = criteria.name;
    }
    if (criteria.filters?.customerChains) {
      criteriaFilter.customerChains = criteria.filters.customerChains;
    }

    if (criteria.filters?.unsubscribeBefore) {
      criteriaFilter.unsubscribeBefore = criteria.filters?.unsubscribeBefore;
    }

    if (criteria.userId) {
      criteriaFilter.userId = criteria.userId;
    }

    if (criteria.is_active) {
      criteriaFilter.is_active = true;
    } else if (criteria.is_active === false) {
      criteriaFilter.is_active = false;
    } else {
      criteriaFilter.is_active = undefined;
    }

    if (criteria.sort) {
      criteriaFilter.sortField = criteria.sort.field;
      criteriaFilter.sortDir = criteria.sort.direction;
    }
    console.log("criteraia filter", criteriaFilter)
    return this.httpClient.post(`${document.location.protocol}//${document.location.hostname}${environment.apiPort}/${ this.languageService.currentLanguage}/customer-locations/export/csv`, criteriaFilter, {
      observe: 'response',
      responseType: 'blob' as 'json',
      headers: {
        Authorization: `Bearer ${this.tokenSessionService.getToken()}`
      }
    });
  }

  public getUserCustomerLocationNotificationPreference(criteria: any): Observable<any>{
    const criteriaFilter = {} as any;

    if (criteria?.filters?.userId) {
      criteriaFilter.userId = criteria?.filters?.userId;
    }

    if (criteria?.filters?.name) {
      criteriaFilter.name = criteria?.filters?.name;
    }

    if (criteria?.filters?.customerLocations) {
      criteriaFilter.customerLocations = criteria?.filters?.customerLocations;
    }

    if (criteria?.filters?.channels?.length>0) {
      criteriaFilter.channels = criteria?.filters?.channels;
    }
    if (criteria?.filters?.customerChains) {
      criteriaFilter.customerChains = criteria.filters?.customerChains;
    }
    criteriaFilter.page = criteria.pagination.page;
    criteriaFilter.perPage = criteria.pagination?.perPage;

    if (criteria.sort) {
      criteriaFilter.sortField = criteria.sort?.field;
      criteriaFilter.sortDir = criteria.sort?.direction;
    }
    return this.httpClient.post(`${document.location.protocol}//${document.location.hostname}${environment.apiPort}/${ this.languageService.currentLanguage}/customer-locations/user-preference`, criteriaFilter, {
      headers: {
        Authorization: `Bearer ${this.tokenSessionService.getToken()}`
      }
    });
  }

  public async getCustomerLocationCategories(): Promise<CustomerLocationCategory[]> {
    return await this.graphqlService
      .query(this.customerLocationCategoriesQuery)
      .pipe(
        take(1),
        map((result: any) => {
          return result.data.getCustomerLocationsCategories;
        })
      )
      .toPromise();
  }

  public async getCustomerLocationForDashboardMapPin(customerLocationId: string): Promise<CustomerLocation> {
    return await this.graphqlService
      .query(this.customerLocationForDashboardMapPinQuery, {customerLocationId}, false)
      .pipe(
        take(1),
        map((result: any) => {
          return result.data.getCustomerLocationForDashboardMapPin;
        })
      )
      .toPromise();
  }

  public async getCustomerLocations(criteria: any): Promise<CustomerLocationsResult> {
    const gqlParams = {} as any;

    if (criteria.filters?.installersOrInstallerChains){
      gqlParams.installersOrInstallerChains = criteria.filters.installersOrInstallerChains;
    }
    if (criteria.name) {
      gqlParams.name = criteria.name;
    }
    if (criteria.filters?.unsubscribeBefore) {
      gqlParams.unsubscribeBefore = criteria.filters?.unsubscribeBefore;
    }
    if (criteria.filters?.customerChains) {
      gqlParams.customerChains = criteria.filters.customerChains;
    }

    if (criteria.userId) {
      gqlParams.userId = criteria.userId;
    }

    if (criteria.is_active) {
      gqlParams.is_active = true;
    } else if (criteria.is_active === false) {
      gqlParams.is_active = false;
    } else {
      gqlParams.is_active = undefined;
    }

    if (criteria.sort) {
      gqlParams.sortField = criteria.sort.field;
      gqlParams.sortDir = criteria.sort.direction;
    }

    if (criteria.pagination) {
      gqlParams.page = criteria.pagination.page;
      gqlParams.perPage = criteria.pagination.perPage;
    }

    return await this.graphqlService
      .query(this.customerLocationsQuery, gqlParams, false)
      .pipe(
        take(1),
        map((result: any) => {
          const customerLocationsResult: CustomerLocationsResult = {
            customerLocationsPage: result.data.getCustomerLocations.itemsPage,
            total: result.data.getCustomerLocations.total
          };
          return customerLocationsResult;
        })
      )
      .toPromise();
  }

  private setFragment(fragment?: any): void {
    this.fragment =
      fragment ||
      gql`
        fragment fields on ${this.modelName} {
          _id
          name
          installer {
            _id
            name
          }
          chain {
            _id
            name
          }
          phone
          email
          country
          countryISO
          postalCode
          city
          latitude
          longitude
          streetAndNumber
          manager {
            _id
            name
          }
          equipments {
            _id
          }
          totalEquipments
        }
      `;
  }
}
